import { useEffect, useId, useRef, useState } from 'react';
import type { SupplierMergeChoice, SupplierMergeSearchResult } from '@tosho/contracts';
import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import {
  supplierConsolidationApi,
  type SupplierConsolidationResult,
} from './supplier-consolidation-api';

interface Props {
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onConsolidated: (result: SupplierConsolidationResult) => void;
}

export function ManualSupplierMergeDialog({
  csrfToken,
  onClose,
  onConsolidated,
}: Props): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId(),
    submitting = useRef(false),
    previousFocus = useRef(typeof document === 'undefined' ? null : document.activeElement);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [first, setFirst] = useState<SupplierMergeChoice | null>(null);
  const [second, setSecond] = useState<SupplierMergeChoice | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const previous = previousFocus.current,
      element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  async function save(): Promise<void> {
    if (submitting.current || first === null || second === null || name.trim() === '') return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await supplierConsolidationApi.consolidateManually(
        {
          displayName: name,
          selections: [
            { groupId: first.groupId, supplierReferenceIds: first.supplierReferenceIds },
            { groupId: second.groupId, supplierReferenceIds: second.supplierReferenceIds },
          ],
        },
        csrfToken,
      );
      onConsolidated(result);
    } catch {
      setError(
        'La fusion n’a pas été enregistrée. Si ces fournisseurs ont changé, revenez aux étapes précédentes pour les rechercher à nouveau.',
      );
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      ref={dialog}
      className="supplier-consolidation supplier-consolidation__dialog supplier-merge"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      close={<ModalCloseButton disabled={busy} onClick={onClose} />}
    >
      <header className="tosho-modal-header">
        <p className="dashboard-panel__eyebrow">Étape {step} / 3</p>
        <h3 id={title}>Fusionner des fournisseurs</h3>
        <p>
          {step === 1
            ? 'Choisissez le premier fournisseur ou un regroupement existant.'
            : step === 2
              ? 'Choisissez le fournisseur à lui rattacher.'
              : 'Vérifiez les fiches et choisissez leur nom commun.'}
        </p>
      </header>
      {step < 3 ? (
        <>
          {step === 2 && first ? (
            <p className="supplier-merge__selection">
              Premier fournisseur : <strong>{first.displayName}</strong>
            </p>
          ) : null}
          <SupplierMergePicker
            key={step}
            selected={step === 1 ? first : second}
            exclude={step === 2 ? first : null}
            onSelect={(choice) => {
              if (step === 1) {
                setFirst(choice);
                setSecond(null);
                if (choice !== null) setName(choice.displayName);
              } else setSecond(choice);
              setError(null);
            }}
          />
        </>
      ) : first && second ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <fieldset disabled={busy}>
            <legend>{first.memberCount + second.memberCount} fiches à réunir</legend>
            {[first, second].map((choice) => (
              <div
                className="supplier-merge__summary"
                key={choice.groupId ?? choice.supplierReferenceIds[0]}
              >
                <strong>{choice.displayName}</strong>
                <ul>
                  {choice.members.map((member) => (
                    <li key={member.id}>{member.displayName}</li>
                  ))}
                </ul>
              </div>
            ))}
          </fieldset>
          <label className="supplier-consolidation__common-name">
            <span>Nom de fournisseur commun</span>
            <input
              required
              maxLength={255}
              value={name}
              disabled={busy}
              onChange={(event) => setName(event.currentTarget.value)}
            />
          </label>
          <p className="supplier-merge__help">
            Les fiches Freebe et leurs achats sont conservés. Vous pourrez défusionner une fiche à
            tout moment.
          </p>
          {error ? (
            <p role="alert" className="tosho-feedback is-error">
              {error}
            </p>
          ) : null}
          <footer>
            <button
              type="button"
              className="is-secondary"
              disabled={busy}
              onClick={() => {
                setError(null);
                setStep(2);
              }}
            >
              Retour
            </button>
            <button type="submit" disabled={busy || name.trim() === ''}>
              {busy ? 'Enregistrement…' : 'Valider la fusion'}
            </button>
          </footer>
        </form>
      ) : null}
      {step < 3 ? (
        <footer>
          <button
            type="button"
            className="is-secondary"
            onClick={() => {
              if (step === 1) onClose();
              else setStep(1);
            }}
          >
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>
          <button
            type="button"
            disabled={step === 1 ? first === null : second === null}
            onClick={() => {
              if (step === 1) setStep(2);
              else setStep(3);
            }}
          >
            Suivant
          </button>
        </footer>
      ) : null}
    </ModalDialog>
  );
}

function SupplierMergePicker({
  selected,
  exclude,
  onSelect,
}: {
  readonly selected: SupplierMergeChoice | null;
  readonly exclude: SupplierMergeChoice | null;
  readonly onSelect: (choice: SupplierMergeChoice | null) => void;
}): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null),
    label = useId();
  const [query, setQuery] = useState(selected?.displayName ?? '');
  const [result, setResult] = useState<SupplierMergeSearchResult | null>(null);
  const [loading, setLoading] = useState(true),
    [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    input.current?.focus();
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const timeout = window.setTimeout(() => {
      void supplierConsolidationApi.search(query, controller.signal).then(
        (next) => {
          if (active) {
            setResult(next);
            setLoading(false);
            setError(false);
          }
        },
        () => {
          if (active) {
            setError(true);
            setLoading(false);
          }
        },
      );
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [query, retry]);
  const choices = (result?.choices ?? []).filter(
    (choice) =>
      !exclude ||
      !choice.supplierReferenceIds.some((id) => exclude.supplierReferenceIds.includes(id)),
  );
  return (
    <div className="supplier-merge__picker">
      <label htmlFor={label}>Rechercher un fournisseur</label>
      <input
        ref={input}
        id={label}
        type="search"
        maxLength={120}
        autoComplete="off"
        placeholder="Nom ou e-mail…"
        value={query}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          setLoading(true);
          setError(false);
          onSelect(null);
        }}
      />
      {loading ? (
        <p role="status">Recherche…</p>
      ) : error ? (
        <p role="alert">
          La recherche n’a pas abouti.{' '}
          <button
            type="button"
            className="is-secondary"
            onClick={() => {
              setLoading(true);
              setError(false);
              setRetry((value) => value + 1);
            }}
          >
            Réessayer
          </button>
        </p>
      ) : (
        <>
          <fieldset className="supplier-merge__choices">
            <legend>Fournisseurs disponibles</legend>
            {choices.map((choice) => (
              <label key={choice.groupId ?? choice.supplierReferenceIds[0]}>
                <input
                  type="radio"
                  name={label}
                  checked={
                    selected !== null &&
                    selected.groupId === choice.groupId &&
                    selected.supplierReferenceIds[0] === choice.supplierReferenceIds[0]
                  }
                  disabled={choice.memberCount + (exclude?.memberCount ?? 0) > 100}
                  onChange={() => onSelect(choice)}
                />
                <span>
                  <strong>{choice.displayName}</strong>
                  <small>
                    {choice.groupId === null
                      ? choice.members[0]?.email
                      : choice.memberCount + ' fiches regroupées'}
                  </small>
                  {choice.groupId !== null ? (
                    <small>{choice.members.map((member) => member.displayName).join(' · ')}</small>
                  ) : null}
                  {choice.memberCount + (exclude?.memberCount ?? 0) > 100 ? (
                    <small>Limite de 100 fiches par fusion atteinte.</small>
                  ) : null}
                </span>
              </label>
            ))}
            {choices.length === 0 ? <p>Aucun autre fournisseur trouvé.</p> : null}
          </fieldset>
          {result?.hasMore ? (
            <p className="supplier-merge__help">
              Affinez la recherche pour voir d’autres fournisseurs.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
